import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Button from "components/button";
import Icon from "components/icon";
import DateRangePicker from "components/date-range-picker";
import { Columns, TableSortState } from "components/table";
import ActionMenu from "components/shared/action-menu";
import { toast } from "react-toastify";
import { useAppDispatch } from "store";
import { setGlobalComponent } from "store/global-components";
import useHistoryPanel from "hooks/shared/useHistoryPanel";
import { useAppTranslation } from "locale/useAppTranslation";
import { editVehicleModelPageRouteName } from "../edit/edit.page";
import {
  useVehicleModelServiceV1ListVehicleModelsQuery,
  useVehicleModelServiceV1ListTollClassesQuery,
  useVehicleModelServiceV1ActivateVehicleModelsMutation,
  useVehicleModelServiceV1DeactivateVehicleModelsMutation,
} from "_services/modules/vehicle-model";
import type { VehicleModelV1ImportRowError } from "_services/modules/vehicle-model";
import { errorHandler } from "_services/errorHandler";
import { useAppSelector } from "store";
import { env } from "config/env";

export interface VehicleModelRow {
  id: string;
  vehicleModel: string;
  vehicleType: string;
  freezerModel: string | null;
  tyreCount: number;
  tyreSize: string | null;
  axleCount: number;
  tollClass: string;
  fuelCapacity: number | null;
  totalFleet: number;
  status: "active" | "inactive";
  createdBy: string;
  createdAt: string;
  updatedBy: string;
  updatedAt: string;
}

const useVehicleModelList = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useAppTranslation("vehicleModel");
  const [selectedKeys, setSelectedKeys] = useState<Array<string | number>>([]);
  const [sortState, setSortState] = useState<TableSortState>(null);
  const [filterValues, setFilterValues] = useState<Record<string, string>>({});
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [bulkAction, setBulkAction] = useState<string>("");
  const [showBulkConfirmModal, setShowBulkConfirmModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importErrors, setImportErrors] = useState<VehicleModelV1ImportRowError[]>([]);
  const [isDownloadingTemplate, setIsDownloadingTemplate] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [showToggleConfirmModal, setShowToggleConfirmModal] = useState(false);
  const token = useAppSelector((s) => s.auth.accessToken);
  const [toggleRow, setToggleRow] = useState<VehicleModelRow | null>(null);
  const historyPanel = useHistoryPanel("vehicle_model");

  const { data: apiResponse, isLoading, refetch } =
    useVehicleModelServiceV1ListVehicleModelsQuery({
      page: currentPage,
      size: rowsPerPage,
      sortBy: sortState?.columnId,
      sortOrder: sortState?.direction,
      status: filterValues.status || undefined,
      vehicleModel: filterValues.vehicleModel || undefined,
      vehicleType: filterValues.vehicleType || undefined,
      tollClassId: filterValues.tollClass || undefined,
      freezerModel: filterValues.freezerModel || undefined,
      tyreCount: filterValues.tyreCount ? Number(filterValues.tyreCount) : undefined,
      tyreSize: filterValues.tyreSize || undefined,
      axleCount: filterValues.axleCount ? Number(filterValues.axleCount) : undefined,
      fuelCapacity: filterValues.fuelCapacity ? Number(filterValues.fuelCapacity) : undefined,
      createdAtFrom: filterValues.createdAt?.split("|")[0] || undefined,
      createdAtTo: filterValues.createdAt?.split("|")[1] || undefined,
      updatedAtFrom: filterValues.updatedAt?.split("|")[0] || undefined,
      updatedAtTo: filterValues.updatedAt?.split("|")[1] || undefined,
    });

  const { data: tollClassesData } = useVehicleModelServiceV1ListTollClassesQuery();

  const [activateVehicleModels] =
    useVehicleModelServiceV1ActivateVehicleModelsMutation();
  const [deactivateVehicleModels] =
    useVehicleModelServiceV1DeactivateVehicleModelsMutation();

  const tableData = useMemo(() => {
    if (!apiResponse?.vehicle_models) return [];
    return apiResponse.vehicle_models.map((item) => ({
      id: item.id || "",
      vehicleModel: item.vehicle_model || "",
      vehicleType: item.vehicle_type_name || "",
      freezerModel: item.freezer_model_name || null,
      tyreCount: item.tyre_count || 0,
      tyreSize: item.tyre_size || null,
      axleCount: item.axle_count || 0,
      tollClass: item.toll_class_name || "",
      fuelCapacity: item.fuel_capacity || null,
      totalFleet: item.total_fleet || 0,
      status: (item.status as "active" | "inactive") || "active",
      createdBy: item.created_by || "",
      createdAt: item.created_at || "",
      updatedBy: item.updated_by || "",
      updatedAt: item.updated_at || "",
    }));
  }, [apiResponse]);

  const tollClassFilterOptions = useMemo(() => {
    const base = [{ label: t("col-all-data"), value: "" }];
    if (!tollClassesData?.toll_classes) return base;
    return [
      ...base,
      ...tollClassesData.toll_classes.map((tc) => ({
        label: tc.name || "",
        value: tc.id || "",
      })),
    ];
  }, [tollClassesData, t]);

  useEffect(() => {
    dispatch(
      setGlobalComponent({ title: t("page-title-list"), hasBackButton: false })
    );
  }, [dispatch, t]);

  // Check if selected rows have mixed status
  const selectedRows = tableData.filter((row) => selectedKeys.includes(row.id));
  const hasMixedStatus =
    selectedRows.length > 0 &&
    new Set(selectedRows.map((r) => r.status)).size > 1;

  const isBulkActionDisabled =
    selectedKeys.length === 0 || hasMixedStatus;

  const handleApplyBulkAction = () => {
    if (!bulkAction || isBulkActionDisabled) return;
    setShowBulkConfirmModal(true);
  };

  const handleConfirmBulkAction = async () => {
    const ids = selectedKeys.map((key) => String(key));
    try {
      if (bulkAction === "activate") {
        await activateVehicleModels({ vehicleModelV1VehicleModelBulkStatusChangeReq: { ids } }).unwrap();
        toast.success(t("toast-activate-success"));
      } else if (bulkAction === "deactivate") {
        await deactivateVehicleModels({ vehicleModelV1VehicleModelBulkStatusChangeReq: { ids } }).unwrap();
        toast.success(t("toast-deactivate-success"));
      }
      setShowBulkConfirmModal(false);
      setBulkAction("");
      setSelectedKeys([]);
    } catch (error) {
      console.error("Bulk action failed:", error);
      errorHandler(error);
    }
  };

  const handleToggleStatus = (row: VehicleModelRow) => {
    setToggleRow(row);
    setShowToggleConfirmModal(true);
  };

  const handleConfirmToggleStatus = async () => {
    if (!toggleRow) return;
    try {
      const ids = [toggleRow.id];
      if (toggleRow.status === "active") {
        await deactivateVehicleModels({ vehicleModelV1VehicleModelBulkStatusChangeReq: { ids } }).unwrap();
        toast.success(t("toast-deactivate-success"));
      } else {
        await activateVehicleModels({ vehicleModelV1VehicleModelBulkStatusChangeReq: { ids } }).unwrap();
        toast.success(t("toast-activate-success"));
      }
      setShowToggleConfirmModal(false);
      setToggleRow(null);
      refetch();
    } catch (error) {
      errorHandler(error);
      console.error("Toggle status failed:", error);
    }
  };

  const handleCloseImportModal = () => {
    setShowImportModal(false);
    setImportFile(null);
    setImportErrors([]);
  };

  const handleDownloadTemplate = async () => {
    setIsDownloadingTemplate(true);
    try {
      const res = await fetch(`${env.REST_HOST}/v1/vehicle-models/import/template`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "vehicle-model-template.xlsx";
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      errorHandler(error);
    } finally {
      setIsDownloadingTemplate(false);
    }
  };

  const handleImport = async () => {
    if (!importFile) return;
    setIsImporting(true);
    try {
      const formData = new FormData();
      formData.append("file", importFile);
      const res = await fetch(`${env.REST_HOST}/v1/vehicle-models/import`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const result = await res.json();
      if (!res.ok) {
        errorHandler(result);
        return;
      }
      if (result.errors?.length) {
        setImportErrors(result.errors);
      } else {
        toast.success(t("import-toast-success", { count: result.imported_count ?? 0 }));
        handleCloseImportModal();
        refetch();
      }
    } catch (error) {
      errorHandler(error);
    } finally {
      setIsImporting(false);
    }
  };

  const handleEdit = (row: VehicleModelRow) => {
    navigate(editVehicleModelPageRouteName(row.id.toString()));
  };

  const columns: Columns<VehicleModelRow>[] = useMemo(
    () => [
      {
        id: "no",
        fieldId: "index",
        label: t("col-no"),
        width: "60px",
      },
      {
        id: "vehicleModel",
        fieldId: "vehicleModel",
        label: t("label-vehicle-model"),
        sortable: true,
        filterable: true,
        filterPlaceholder: t("col-search-placeholder"),
      },
      {
        id: "vehicleType",
        fieldId: "vehicleType",
        label: t("label-vehicle-type"),
        sortable: true,
        filterable: true,
        filterPlaceholder: t("col-search-placeholder"),
      },
      {
        id: "freezerModel",
        fieldId: "freezerModel",
        label: t("label-freezer-model"),
        sortable: true,
        filterable: true,
        filterPlaceholder: t("col-search-placeholder"),
        render: (row) => (
          <span>{row.freezerModel ?? "-"}</span>
        ),
      },
      {
        id: "totalFleet",
        fieldId: "totalFleet",
        label: t("label-total-fleet"),
        sortable: true,
        filterable: true,
        filterPlaceholder: t("col-search-placeholder"),
      },
      {
        id: "tyreCount",
        fieldId: "tyreCount",
        label: t("label-tyre-count"),
        sortable: true,
        filterable: true,
        filterPlaceholder: t("col-search-placeholder"),
      },
      {
        id: "tyreSize",
        fieldId: "tyreSize",
        label: t("label-tyre-size"),
        sortable: true,
        filterable: true,
        filterPlaceholder: t("col-search-placeholder"),
        render: (row) => (
          <span>{row.tyreSize ?? "-"}</span>
        ),
      },
      {
        id: "axleCount",
        fieldId: "axleCount",
        label: t("label-axle-count"),
        sortable: true,
        filterable: true,
        filterPlaceholder: t("col-search-placeholder"),
      },
      {
        id: "tollClass",
        fieldId: "tollClass",
        label: t("label-toll-class"),
        sortable: true,
        filterable: true,
        filterOptions: tollClassFilterOptions,
      },
      {
        id: "fuelCapacity",
        fieldId: "fuelCapacity",
        label: t("label-fuel-capacity"),
        sortable: true,
        filterable: true,
        filterPlaceholder: t("col-search-placeholder"),
        render: (row) => (
          <span>{row.fuelCapacity ?? "-"}</span>
        ),
      },
      {
        id: "status",
        fieldId: "status",
        label: t("label-status"),
        sortable: true,
        filterable: true,
        filterOptions: [
          { label: t("col-all-data"), value: "" },
          { label: t("status-active"), value: "active" },
          { label: t("status-inactive"), value: "inactive" },
        ],
        render: (row) => (
          <div className="inline-flex items-center gap-2 text-sm" data-testid={`col-status-${row.id}`}>
            <span
              className={`h-3 w-3 rounded-full ${
                row.status === "active"
                  ? "bg-primary-600 border-2 border-primary-100"
                  : "bg-greyScale-60 border-2 border-greyScale-90"
              }`}
            />
            <span className="capitalize">{row.status}</span>
          </div>
        ),
      },
      {
        id: "createdAt",
        fieldId: "createdBy",
        fieldId2: "createdAt",
        label: t("col-created-by"),
        sortable: true,
        filterable: true,
        renderFilter: ({ value, onChange }) => {
          const [from = "", to = ""] = value.split("|");
          return (
            <DateRangePicker
              fromValue={from}
              toValue={to}
              onChange={(f, t) => onChange(`${f}|${t}`)}
              reserveHelperSpace={false}
            />
          );
        },
      },
      {
        id: "updatedAt",
        fieldId: "updatedBy",
        fieldId2: "updatedAt",
        label: t("col-updated-by"),
        sortable: true,
        filterable: true,
        renderFilter: ({ value, onChange }) => {
          const [from = "", to = ""] = value.split("|");
          return (
            <DateRangePicker
              fromValue={from}
              toValue={to}
              onChange={(f, t) => onChange(`${f}|${t}`)}
              reserveHelperSpace={false}
            />
          );
        },
      },
      {
        id: "action",
        fieldId: "id",
        label: t("col-action"),
        width: "140px",
        dragDisabled: true,
        render: (row) => (
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              color="primary"
              size="small"
              iconLeft={<Icon name="edit-2" size={14} />}
              onClick={() => handleEdit(row)}
              data-testid="btn-edit-model"
            >
              {t("btn-edit")}
            </Button>
            <ActionMenu
              items={[
                {
                  label:
                    row.status === "active" ? t("btn-deactivate") : t("btn-activate"),
                  onClick: () => handleToggleStatus(row),
                  variant:
                    row.status === "active" ? "danger" : "default",
                  testId:
                    row.status === "active"
                      ? "btn-deactivate-model-list"
                      : "btn-activate-model-list",
                },
                {
                  label: t("btn-track-history"),
                  onClick: () =>
                    historyPanel.openPanel(row.id, row.vehicleModel),
                  testId: "btn-track-history-model-list",
                },
              ]}
            />
          </div>
        ),
      },
    ],
    [tollClassFilterOptions, t],
  );

  const totalPages = apiResponse?.metadata?.total_page ?? 1;

  const handleFilterChange = (values: Record<string, string>) => {
    setFilterValues(values);
    setCurrentPage(1);
  };

  return {
    data: tableData,
    columns,
    sortState,
    setSortState,
    filterValues,
    setFilterValues,
    handleFilterChange,
    selectedKeys,
    setSelectedKeys,
    currentPage,
    setCurrentPage,
    rowsPerPage,
    setRowsPerPage,
    totalPages,
    navigate,
    bulkAction,
    setBulkAction,
    isBulkActionDisabled,
    showBulkConfirmModal,
    setShowBulkConfirmModal,
    handleApplyBulkAction,
    handleConfirmBulkAction,
    showImportModal,
    setShowImportModal,
    importFile,
    setImportFile,
    importErrors,
    isDownloadingTemplate,
    isImporting,
    handleDownloadTemplate,
    handleImport,
    handleCloseImportModal,
    showToggleConfirmModal,
    setShowToggleConfirmModal,
    toggleRow,
    handleConfirmToggleStatus,
    historyPanel,
    isLoading,
  };
};

export default useVehicleModelList;
